import React from 'react';
import { processData } from '../data/mock';
import useScrollAnimation from '../hooks/useScrollAnimation';
import FloatingParticles from './graphics/FloatingParticles';

export const ProcessSection = () => {
  const [headerRef, headerVisible] = useScrollAnimation({ threshold: 0.2 });
  const [flowRef, flowVisible] = useScrollAnimation({ threshold: 0.15 });

  const steps = processData.steps;

  return (
    <section id="process" className="bg-gradient-to-b from-[#0f1a2a] to-[#1a2435] py-24 lg:py-32 relative overflow-hidden">
      {/* Background */}
      <div className="noise-overlay" />
      <FloatingParticles count={15} />
      
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-[#5a7fa8] opacity-[0.02] blur-[100px] rounded-full" />
      <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] bg-[#c9a87c] opacity-[0.02] blur-[80px] rounded-full" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative z-10">
        {/* Section Header */}
        <div 
          ref={headerRef}
          className={`text-center mb-16 lg:mb-24 animate-on-scroll ${headerVisible ? 'is-visible' : ''}`}
        >
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#f5f5f0] leading-tight mb-6">
            {processData.sectionTitle}
          </h2>
          <p className="text-base text-[#6b7c93] italic max-w-xl mx-auto">
            {processData.intro}
          </p>
        </div>

        {/* Process Flow - Interactive Timeline */}
        <div
          ref={flowRef}
          className={`relative ${flowVisible ? '' : ''}`}
        >
          {/* Desktop Horizontal Flow */}
          <div className="hidden lg:block">
            {/* Connection Line */}
            <div className="absolute top-24 left-[10%] right-[10%] h-px bg-[#2a3a4d]">
              <div 
                className={`h-full bg-gradient-to-r from-[#5a7fa8] via-[#c9a87c] to-[#5a7fa8] transition-all duration-[2s] ease-out ${flowVisible ? 'w-full' : 'w-0'}`}
                style={{ transitionDelay: '0.5s' }}
              />
            </div>

            {/* Steps */}
            <div className="grid grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`text-center transition-all duration-700 ${flowVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${600 + index * 200}ms` }}
                >
                  {/* Node */}
                  <div className="relative inline-flex items-center justify-center mb-8">
                    {/* Outer ring */}
                    <div className="absolute w-20 h-20 rounded-full border border-[#2a3a4d] group-hover:border-[#5a7fa8] transition-colors duration-300" />
                    {/* Middle ring */}
                    <div className={`absolute w-14 h-14 rounded-full border transition-all duration-500 ${flowVisible ? 'border-[#5a7fa8]/50 scale-100' : 'border-transparent scale-0'}`} style={{ transitionDelay: `${800 + index * 200}ms` }} />
                    {/* Inner circle */}
                    <div className="relative w-12 h-12 rounded-full bg-[#1a2435] border border-[#5a7fa8] flex items-center justify-center group hover:bg-[#5a7fa8]/10 transition-all duration-300">
                      <span className="text-sm font-medium text-[#c9a87c]">{String(index + 1).padStart(2, '0')}</span>
                    </div>
                    {/* Pulse effect */}
                    <div className={`absolute w-12 h-12 rounded-full bg-[#5a7fa8] transition-all duration-1000 ${flowVisible ? 'opacity-0 scale-150' : 'opacity-20 scale-100'}`} style={{ transitionDelay: `${1000 + index * 200}ms` }} />
                  </div>

                  {/* Content */}
                  <h3 className="text-xs tracking-[0.2em] text-[#c9a87c] uppercase mb-3">{step.title}</h3>
                  <p className="text-sm text-[#6b7c93] leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Vertical Flow */}
          <div className="lg:hidden">
            <div className="relative pl-8">
              {/* Vertical Line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-[#2a3a4d]">
                <div 
                  className={`w-full bg-gradient-to-b from-[#5a7fa8] via-[#c9a87c] to-[#5a7fa8] transition-all duration-[2s] ease-out ${flowVisible ? 'h-full' : 'h-0'}`}
                  style={{ transitionDelay: '0.3s' }}
                />
              </div>

              {/* Steps */}
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className={`relative pb-12 last:pb-0 transition-all duration-500 ${flowVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}
                  style={{ transitionDelay: `${400 + index * 150}ms` }}
                >
                  {/* Node */}
                  <div className="absolute left-0 -translate-x-1/2 w-6 h-6 rounded-full bg-[#1a2435] border border-[#5a7fa8] flex items-center justify-center">
                    <div className="w-2 h-2 rounded-full bg-[#c9a87c]" />
                  </div>
                  
                  {/* Content */}
                  <div className="ml-8">
                    <h3 className="text-xs tracking-[0.2em] text-[#c9a87c] uppercase mb-2">{step.title}</h3>
                    <p className="text-sm text-[#6b7c93] leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
